import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useParams } from "react-router-dom";
import { LocalizedLink as Link } from "../components/LocalizedLink";
import { Droplet, Sparkle, X } from "lucide-react";
import { useMessages } from "../context/MessagesContext";
import { getSuerosData } from "../lib/suerosData";

function formatSuggested(text?: string) {
  if (!text) return null;

  if (text.startsWith("Recomendado")) {
    return (
      <>
        <span className="font-bold">Recomendado</span>
        {text.replace("Recomendado", "")}
      </>
    );
  }

  if (text.startsWith("Suggested")) {
    return (
      <>
        <span className="font-bold">Suggested</span>
        {text.replace("Suggested", "")}
      </>
    );
  }

  return text;
}

export default function SueroDetalle() {
  const { slug = "" } = useParams();
  const labels = useMessages();
  const sueros = getSuerosData(labels);
  const suero = sueros.find((item) => item.slug === slug.toLowerCase());
  const [selectedImage, setSelectedImage] = useState<{
    src: string;
    alt: string;
  } | null>(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [slug]);

  useEffect(() => {
    if (!selectedImage) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setSelectedImage(null);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedImage]);

  if (!suero) {
    return (
      <section className="py-16 px-4">
        <div className="section-surface max-w-2xl mx-auto p-6 text-center space-y-4">
          <h1 className="text-2xl font-bold text-gray-800">Suero no encontrado</h1>
          <p className="text-gray-600">
            No encontramos un suero para la URL <span className="font-semibold">/sueros/{slug}</span>.
          </p>
          <Link
            to="/sueros"
            className="inline-flex items-center justify-center rounded-md bg-cyan-600 px-4 py-2 text-sm font-semibold text-white hover:bg-cyan-700 transition-colors"
          >
            Ver todos los sueros
          </Link>
        </div>
      </section>
    );
  }

  const related = sueros.filter((item) => item.slug !== suero.slug).slice(0, 3);
  const localIntro = (
    labels["sueros.detail.localIntro"] ||
    "Sueroterapia intravenosa en Tijuana con valoración previa y aplicación profesional en Hidromed MX."
  ).replace("{title}", suero.title);

  return (
    <section id="sueros" className="scroll-mt-24 py-8">
      <div className="max-w-8xl mx-auto px-4 space-y-6">
        <nav className="text-sm text-gray-500">
          <Link to="/" className="hover:underline">
            {labels["nav.home"] || "Inicio"}
          </Link>
          <span className="mx-2">/</span>
          <Link to="/sueros" className="hover:underline">
            {labels["nav.sueros"] || "Sueros"}
          </Link>
          <span className="mx-2">/</span>
          <span className="text-gray-700 font-semibold">{suero.title}</span>
        </nav>

        <div className={`section-surface ${suero.bgCard} p-6 md:p-8`}>
          <div className="flex flex-col gap-5">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${suero.bgColor}`}>
                  <Droplet className={`w-6 h-6 ${suero.iconColor}`} />
                </div>
                <h1 className="text-3xl md:text-4xl font-bold text-gray-900">{suero.title}</h1>
              </div>

              <div
                className={`rounded-md ${suero.bgColor} px-3 py-2 text-sm font-semibold text-gray-900`}
              >
                {suero.price.join(" ")}
              </div>
            </div>

            <p className="lead-copy text-slate-700">{localIntro}</p>

            <div className="overflow-hidden rounded-2xl bg-white/70 h-56 sm:h-64 md:h-80 flex items-center justify-center">
              <button
                type="button"
                onClick={() => setSelectedImage({ src: suero.image, alt: suero.title })}
                className="w-full h-full"
                aria-label={`Ver imagen de ${suero.title}`}
              >
                <img
                  src={suero.image}
                  alt={suero.title}
                  className="w-full h-full object-contain rounded-2xl"
                  loading="eager"
                />
              </button>
            </div>

            <div className="space-y-2">
              {suero.options.map((option, index) => (
                <p key={index} className="text-gray-700">
                  {option}
                </p>
              ))}
            </div>

            <div className="grid gap-2">
              {suero.description.map((benefit, index) => (
                <div key={index} className="flex items-start gap-2 text-gray-800">
                  <div className="w-2 h-2 bg-cyan-500 rounded-full mt-2 flex-shrink-0" />
                  <span>{benefit}</span>
                </div>
              ))}
            </div>

            {suero.suggested && (
              <div className="flex items-start gap-2 bg-white/60 rounded-lg p-3">
                <Sparkle className="w-5 h-5 text-yellow-500 fill-current shrink-0 mt-0.5" />
                <p className="body-copy">{formatSuggested(suero.suggested)}</p>
              </div>
            )}

            {suero.backInfo && <p className="body-copy leading-relaxed">{suero.backInfo}</p>}

            <div className="flex flex-wrap gap-3 pt-2">
              <Link
                to="/reservar"
                className="inline-flex items-center justify-center rounded-md bg-cyan-600 px-4 py-2 text-sm font-semibold text-white hover:bg-cyan-700 transition-colors"
              >
                {labels["reserve.suero"] || "Reservar este suero"}
              </Link>
              <Link
                to="/sueros"
                className="inline-flex items-center justify-center rounded-md border border-cyan-600 px-4 py-2 text-sm font-semibold text-cyan-700 hover:bg-cyan-50 transition-colors"
              >
                {labels["reserve.sueros"] || "Ver todos los sueros"}
              </Link>
            </div>
          </div>
        </div>

        <div className="section-surface p-4 md:p-5">
          <h2 className="text-lg font-semibold text-gray-900 mb-3">{labels["reserve.suero.relacionado"] || "Sueros relacionados"}</h2>
          <div className="flex flex-wrap gap-2">
            {related.map((item) => (
              <Link
                key={item.slug}
                to={`/sueros/${item.slug}`}
                className="rounded-full bg-cyan-100 text-cyan-800 px-3 py-1 text-sm hover:bg-cyan-200 transition-colors"
              >
                {item.title}
              </Link>
            ))}
          </div>
        </div>
      </div>

      {selectedImage
        ? createPortal(
          <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
            onClick={() => setSelectedImage(null)}
            role="dialog"
            aria-modal="true"
            aria-label={selectedImage.alt}
          >
            <div
              className="relative max-h-[90vh] max-w-[90vw]"
              onClick={(event) => event.stopPropagation()}
            >
              <button
                type="button"
                onClick={() => setSelectedImage(null)}
                className="absolute -top-3 -right-3 rounded-full bg-white/90 p-2 text-slate-700 shadow-md"
                aria-label="Cerrar imagen"
              >
                <X className="h-5 w-5" />
              </button>

              <img
                src={selectedImage.src}
                alt={selectedImage.alt}
                className="max-h-[90vh] max-w-[90vw] rounded-2xl bg-white object-contain"
              />
            </div>
          </div>,
          document.body
        )
        : null}
    </section>
  );
}
